import { Fragment } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import ContentWrapper from '~/tutor-on-demand/components/Content/ContentWrapper'
import HeaderTitle from '~/tutor-on-demand/components/Header/HeaderTitle'
import HeaderWrapper from '~/tutor-on-demand/components/Header/HeaderWrapper'
import List from '~/tutor-on-demand/components/Typography/List'
import TutorOnDemandProductCards from '~/shared/components/TutorOnDemandProductCards/TutorOnDemandProductCards.component'
import Paragraph from '~/tutor-on-demand/components/Typography/Paragraph'
import SubContainer from '~/tutor-on-demand/components/Content/SubContainer'
import SubContainerWrapper from '~/tutor-on-demand/components/Content/SubContainerWrapper'
import { SubjectType, getSubjectImage } from '~/components/Metadata/SubjectMetatags'

import ROUTES from '~/shared/constants/routes.const'

import BreadCrumbs from '~/encyclopedia/components/BreadCrumbs'
import { Link } from '~/components'

import { mathTopic } from '../types/tutor-on-demand-math.types'
import { mathTopicLinks } from '../constants/TutorOnDemandMathList.constants'

const mathLevels = [
  'Matematikk 1P MAT1011',
  'Matematikk 2P MAT1015',
  'Matematikk R1/REA 3022',
  'Matematikk R2/REA 3024',
  'Matematikk S1/REA 3026',
  'Matematikk S2/REA 3028',
]

export default function TutorOnDemandMath({ type }: { type: SubjectType }): React.ReactElement {
  const { locale } = useRouter()
  const { t } = useTranslation(['tutorOnDemand', 'tutorOnDemandMath'])
  const breadcrumbs = [
    { path: ROUTES.TUTOR_ON_DEMAND_INFORMATION, h1: t('tutorOnDemand:landingPage.whatIsTutorOnDemand.title') },
    { path: ROUTES.TUTOR_ON_DEMAND_MATH, h1: t('tutorOnDemandMath:title') },
  ]

  return (
    <Fragment>
      <HeaderWrapper>
        <BreadCrumbs
          breadcrumbs={breadcrumbs}
          fontSize="medium"
          isWhite
          itemClassNames="font-medium items-center"
          wrapperClassNames="mt-1 pr-12 lg:pr-0"
        />

        <HeaderTitle
          iconAlt={getSubjectImage(type, t).alt as string}
          iconSrc={getSubjectImage(type, t).srcKey as string}
        >
          {t('tutorOnDemandMath:title')}
        </HeaderTitle>
      </HeaderWrapper>
      <ContentWrapper className="space-y-8">
        <div className="xl:space-x-8 xl:flex">
          <section className="mb-6 space-y-6 xl:mb-0">
            <Paragraph>{t('tutorOnDemandMath:contentParagraph1')}</Paragraph>
            <Paragraph>{t('tutorOnDemandMath:contentParagraph2')}</Paragraph>
            <Paragraph>{t('tutorOnDemandMath:contentParagraph3')}</Paragraph>
            <Paragraph>{t('tutorOnDemandMath:contentParagraph4')}</Paragraph>
            <Paragraph>{t('tutorOnDemandMath:contentParagraph5')}</Paragraph>
          </section>
          <SubContainerWrapper>
            <SubContainer className="w-full max-w-sm" header={t('tutorOnDemandMath:weCanHelpYouWith.header')}>
              <ul className="ml-6 list-disc list-outside text-black-light">
                {Object.values(mathTopic).map((value) => (
                  <li key={value}>
                    <Link className="underline font-medium text-purple" to={mathTopicLinks[value][locale]}>
                      {t(`tutorOnDemandMath:weCanHelpYouWith.items.${value}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </SubContainer>
            {locale === 'no' && (
              <SubContainer className="w-full max-w-sm" header="Vi underviser i alle fag, som for eksempel">
                <List items={mathLevels} />
              </SubContainer>
            )}
          </SubContainerWrapper>
        </div>
        <TutorOnDemandProductCards
          className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
          colorScheme="white"
          hideChatWithTutor
          hideVideoChatWithTutor
        />
      </ContentWrapper>
    </Fragment>
  )
}
